<template>
  <KkLoading v-if="customSurveysLoading" />
  <div v-else-if="customSurveys.length === 0">
    <KkTutorial
      :description1="$t('pages.CompaniesSurveys.tutorial.noSurvey1')"
      :description2="$t('pages.CompaniesSurveys.tutorial.noSurvey2')"
      :img-path="tutorialImgPath"
      :tutorial-description="$t('pages.CompaniesSurveys.tutorial.description')"
    >
      <template v-if="showOnlyOwnerAuthority" #buttonArea>
        <KkButton
          color="primary"
          :text="$t('pages.CompaniesSurveys.tutorial.createSurvey')"
          :to="{ name: 'CompaniesSurveysNew', params: { company_uuid: companyUuid }}"
        />
      </template>
    </KkTutorial>
  </div>
  <div v-else>
    <div class="p-box">
      <div class="p-box__header">
        <p v-if="customSurveysMeta.unsetBrands.length === 0">{{ $t('pages.CompaniesSurveys.noUnsetBrand') }}</p>
        <p v-else>{{ $t('pages.CompaniesSurveys.unsetBrand') }}<a class="unsetBrandsLink" @click="showUnsetBrandsModal">{{ customSurveysMeta.unsetBrands.length }}</a>{{ $t('pages.CompaniesSurveys.brandNotSelected') }}</p>
        <div v-if="showOnlyOwnerAuthority">
          <KkButton
            color="primary"
            :text="$t('pages.CompaniesSurveys.createSurvey')"
            :to="{ name: 'CompaniesSurveysNew', params: { company_uuid: companyUuid }}"
          />
        </div>
      </div>
      <div class="p-box__body">
        <div class="p-table__content">
          <KkInfo>
            <p>
              {{ $t('pages.CompaniesSurveys.note1') }}<br>
              {{ $t('pages.CompaniesSurveys.note2') }}<br>
              {{ $t('pages.CompaniesSurveys.note3') }}
            </p>
          </KkInfo>
          <table class="surveyTemplateTable">
            <thead>
              <tr>
                <th>
                  {{ $t('pages.CompaniesSurveys.templateName') }}
                </th>
                <th>
                  {{ $t('pages.CompaniesSurveys.selectedBrands') }}
                </th>
                <th>
                  {{ $t('pages.CompaniesSurveys.publishedInfo') }}
                </th>
                <th>
                  {{ $t('pages.CompaniesSurveys.publishedStatus.title') }}
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(survey, i) in customSurveys"
                :key="'surveyTemplate_' + i"
                class="surveyTemplateTable__tr"
                :class="{ 'surveyTemplateTable__tr--closed': isClosed(survey.status) }"
              >
                <td class="templateName u-fwB">
                  {{ survey.name }}
                </td>
                <td>
                  <template v-if="survey.brands.length !== 0">
                    <div>
                      <ul class="brands">
                        <li
                          v-for="( brand, j ) in survey.brands.slice(0, showBrandImageAmount)"
                          :key="`brand_${j}`"
                          class="brands__item"
                          :class="{ 'brands__item--disable': isDisabledBrand(brand) }"
                        >
                          <span>
                            <img :src="brand.imageUrl" alt="">
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <a class="setBrandsLink" @click="showSurveyBrandsModal(survey.id)">{{ $t('pages.CompaniesSurveys.confirmSelectedBrands', { count: survey.brands.length }) }}</a>
                    </div>
                  </template>
                  <template v-else>
                    {{ $t('pages.CompaniesSurveys.targetBrandNotSelected') }}
                  </template>
                </td>
                <td>
                  <div class="publishRange">
                    <p v-if="!isUnpublished(survey.status)" class="publishRange__row">
                      {{ publishRange(survey) }}
                    </p>
                    <p :class="{ 'unpublished': isUnpublished(survey.status) }" class="publishRange__row">
                      <StatusBadge :status="extractStatusNumber(survey.status)" />
                      {{ $t('pages.CompaniesSurveys.publishedStatus.' + survey.status) }}
                    </p>
                    <template v-if="!isUnpublished(survey.status)">
                      <KkInfo v-if="canEditAll(survey)" class="publishRange__row">
                        {{ $t('pages.CompaniesSurveys.canEditAll') }}
                      </KkInfo>
                      <KkInfo v-else-if="canNotEditOnlyQuestion(survey)" class="publishRange__row">
                        {{ $t('pages.CompaniesSurveys.canEditExceptQuestion') }}
                      </KkInfo>
                    </template>
                  </div>
                </td>
                <td>
                  <div class="answerCountBox">
                    {{ $t('pages.CompaniesSurveys.answerCount') }}：
                    <div>
                      <span v-if="survey.status === 'unpublished'" class="answerCountBox__count">
                        -
                      </span>
                      <span v-else-if="survey.answerCount === 0" class="answerCountBox__count">
                        0
                      </span>
                      <router-link v-else :to="{ name: 'CompaniesCustomSurveyAnswers', params: { company_uuid: companyUuid }, query: { custom_survey_id: survey.id } }">
                        <span class="answerCountBox__count">
                          {{ survey.answerCount }}
                        </span>
                      </router-link>
                      {{ $t('pages.CompaniesSurveys.answerCountUnit') }}
                    </div>
                  </div>
                  <p v-if="survey.answerCount" class="analysisLink">
                    <router-link :to="{ name: 'CompaniesAnalysisSurveys', params: { company_uuid: companyUuid }, query: { custom_survey_id: survey.id } }">
                      {{ $t('pages.CompaniesSurveys.analysisLink') }}
                    </router-link>
                  </p>
                  <p v-else-if="displayAnswerFilesLink(survey)">
                    <router-link :to="{ name: 'CompaniesCustomSurveyAnswerFiles', params: { company_uuid: companyUuid, custom_survey_id: survey.id } }">
                      {{ $t('pages.CompaniesSurveys.importAnswerFileLink') }}
                    </router-link>
                  </p>
                </td>
                <td>
                  <div class="actionBox">
                    <p v-if="survey.status !== 'closed'">
                      <router-link :to="{ name: 'CompaniesSurveysEdit', params: { company_uuid: companyUuid, survey_id: survey.id }}" class="action">
                        <KkIcon name="edit" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
                        {{ $t('labels.edit') }}
                      </router-link>
                    </p>
                    <p>
                      <button class="action" @click="openPreview(survey.id)">
                        <KkIcon name="detail" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
                        {{ $t('labels.preview') }}
                      </button>
                    </p>
                    <p>
                      <button class="action" @click="copySurvey(survey.id)">
                        <KkIcon name="copy" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
                        {{ $t('labels.copy') }}
                      </button>
                    </p>
                    <p v-if="showOnlyOwnerAuthority && survey.deletable">
                      <button class="action" @click="handleDelete(survey.id)">
                        <KkIcon name="delete" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
                        {{ $t('labels.delete') }}
                      </button>
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="p-controller">
          <div class="p-controller__count">
            <p class="p-controller__count__name">
              {{ $t('labels.perPageCount') }}
            </p>
            <div class="p-controller__count__select">
              <select :value="displayNumber" @change="changeDisplayNumber($event)">
                <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
              </select>
            </div>
            <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
          </div>
          <div class="p-controller__navi">
            <button
              type="button"
              :disabled="customSurveysLoading || currentPage === 1"
              class="p-controller__navi__prev"
              @click="goPrevPage"
            >
              <KkIcon name="arrowLeft" color="rgba(58, 59, 63, 0.7)" />
            </button>
            <button
              type="button"
              :disabled="customSurveysLoading || currentPage >= customSurveysMeta.totalPages"
              class="p-controller__navi__next"
              @click="goNextPage"
            >
              <KkIcon name="arrowRight" color="rgba(58, 59, 63, 0.7)" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Modal v-if="isPreview" width="375px" @close="closePreview">
    <KkLoading v-if="customSurveyLoading" />
    <SurveyInputFormPreview v-else :survey="customSurvey" />
  </Modal>
  <KkItemListModal
    :is-visible="openUnsetBrandsModal"
    :header-label="$t('pages.CompaniesSurveys.listOfUnsetBrand')"
    :item-list="customSurveysMeta.unsetBrands"
    @close="hideUnsetBrandsModal"
  />
  <KkSurveyBrandsModal
    :is-visible="openSurveyBrandsModal"
    :loading="customSurveyBrandsLoading"
    :brands="customSurveyBrands"
    @close="hideSurveyBrandsModal"
  />
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import StatusBadge from '../components/StatusBadge'
import Modal from '../components/Modal'
import SurveyInputFormPreview from '../components/SurveyInputFormPreview'
import { basicDateFormat } from '../modules/date'
import {
  KkButton,
  KkLoading,
  KkIcon
} from '../components/base'
import { KkInfo } from '../components/ui'
import { KkTutorial, KkItemListModal, KkSurveyBrandsModal } from '../components/projects'
import tutorialImgPath from '../../../images/surveys/tutorial.png'

export default {
  name: 'CompaniesSurveys',
  components: {
    StatusBadge,
    Modal,
    SurveyInputFormPreview,
    KkButton,
    KkLoading,
    KkIcon,
    KkInfo,
    KkTutorial,
    KkItemListModal,
    KkSurveyBrandsModal,
  },
  data() {
    return {
      isPreview: false,
      openSurveyBrandsModal: false,
      openUnsetBrandsModal: false,
      currentPage: 1,
    }
  },
  computed: {
    ...mapState({
      customSurveysLoading: state => state.companiesCustomSurveys.loading,
      customSurveys: state => state.companiesCustomSurveys.data,
      customSurveysMeta: state => state.companiesCustomSurveys.meta,
      customSurveyLoading: state => state.companiesCustomSurvey.loading,
      customSurvey: state => state.companiesCustomSurvey.data,
      customSurveyBrandsLoading: state => state.companiesCustomSurveyBrands.loading,
      customSurveyBrands: state => state.companiesCustomSurveyBrands.data,
      tutorialImgPath: () => tutorialImgPath,
      displayNumber: state => state.filters.displayNumber,
    }),
    ...mapGetters({
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
    }),
    companyUuid() {
      return this.$route.params.company_uuid
    },
    showBrandImageAmount() {
      return 3
    },
    startDataNumber() { return (this.currentPage - 1) * this.displayNumber + 1 },
    endDataNumber() { return this.startDataNumber + this.customSurveys.length - 1 },
    readCustomSurveysParams() {
      return {
        page: this.currentPage,
        per: this.displayNumber,
      }
    }
  },
  created() {
    this.handleSubmit()
  },
  methods: {
    ...mapActions({
      readCustomSurveys: 'companiesCustomSurveys/readCustomSurveys',
      readCustomSurvey: 'companiesCustomSurvey/readCustomSurvey',
      readCustomSurveyBrands: 'companiesCustomSurveyBrands/readCustomSurveyBrands',
      deleteCustomSurvey: 'companiesCustomSurveys/deleteCustomSurvey',
      setDisplayNumber: 'filters/setDisplayNumber',
    }),
    extractStatusNumber(status) {
      switch (status) {
      case 'published':
        return 1
      case 'unpublished':
        return 2
      case 'closed':
        return 3
      }
    },
    // 質問だけ編集不可能、それ以外は編集可能
    canNotEditOnlyQuestion(survey) {
      return survey.status !== 'closed'
        && survey.basicInfoEditable
        && survey.completeEditable
        && !survey.questionEditable
    },
    canEditAll(survey) {
      return survey.status !== 'closed'
        && survey.basicInfoEditable
        && survey.completeEditable
        && survey.questionEditable
    },
    publishRange(survey) {
      if (!survey.closedAt) return this.$t('pages.CompaniesSurveys.publishedAt', { date: basicDateFormat(survey.publishedAt) })
      return basicDateFormat(survey.publishedAt) + ' - ' + basicDateFormat(survey.closedAt)
    },
    isUnpublished(status) {
      return status === 'unpublished'
    },
    isClosed(status) {
      return status === 'closed'
    },
    isDisabledBrand(brand) {
      return ['closed', 'unpublished'].includes(brand.status)
    },
    handleDelete(id) {
      this.deleteCustomSurvey({ surveyId: id }).then(() => {
        this.readCustomSurveys()
      })
    },
    copySurvey(id) {
      this.$router.push({ name: 'CompaniesSurveysNew', params: { company_uuid: this.companyUuid }, query: { copySurveyId: id }})
    },
    openPreview(id) {
      this.readCustomSurvey(id)
      this.isPreview = true
    },
    closePreview() {
      this.isPreview = false
    },
    displayAnswerFilesLink(survey) {
      return !this.isUnpublished(survey.status) && survey.answerCount === 0
    },
    showSurveyBrandsModal(survey_id) {
      this.readCustomSurveyBrands(survey_id)
      this.openSurveyBrandsModal = true
    },
    hideSurveyBrandsModal() {
      this.openSurveyBrandsModal = false
    },
    showUnsetBrandsModal() {
      this.openUnsetBrandsModal = true
    },
    hideUnsetBrandsModal() {
      this.openUnsetBrandsModal = false
    },
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    /**
     * 表示件数の変更
     * @param  { Object } e
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(Number(e.target.value))
      this.handleResetPageAndSubmit()
    },
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    handleSubmit() {
      this.readCustomSurveys(this.readCustomSurveysParams)
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/biz_spa/foundation/_var.scss";
@import "../../../stylesheets/biz_spa/foundation/_mixin.scss";
.p-box {
  &__header {
    justify-content: space-between;
  }
}
.unpublished {
  color: $color-accent-primary;
}
.analysisLink {
  margin-top: 8px;
}
.unsetBrandsLink {
  padding-right: 4px;
  padding-left: 8px;
  color: $color-accent-primary;
  font-weight: bold;
  font-size: 24px;
  cursor: pointer;
}
.setBrandsLink {
  cursor: pointer;
}
.answerCountBox {
  padding: 8px 50px;
  border-radius: 4px;
  background: #ECEDF0;
  font-weight: bold;
  text-align: center;
  &__count {
    margin-top: 8px;
    font-size: 18px;
  }
}
.actionBox {
  a {
    text-decoration: none;
  }
  p {
    text-align: center;
  }
  p:not(:first-child) {
    margin-top: 16px;
  }
}
.publishRange__row:not(:first-child) {
  margin-top: 8px;
}
.action {
  svg {
    vertical-align: text-bottom;
  }
  display: flex;
  &__icon {
    width: 16px;
    margin-right: 8px;
  }
}
.surveyTemplateTable {
  border-spacing: 0 8px !important;
  thead {
    display: none;
  }
  td {
    display: block;
    width: 100%;
    border: 1px solid #E4E7EB;
    font-size: 12px;
  }
  td:not(:first-child) {
    border-top: none;
  }
  td:not(:last-child) {
    border-bottom: none;
  }
  &__tr {
    margin-top: 16px;
  }
  &__tr--closed td {
    background: $color-base-i;
  }
  .templateName {
    font-size: 14px;
  }
  @include mq() {
    thead {
      display: table-header-group;
    }
    td {
      display: table-cell;
      width: auto;
    }
    &__tr {
      vertical-align: top;
    }
    td:not(:first-child) {
      border-top: 1px solid #E4E7EB;
      border-left: none;
    }
    td:not(:last-child) {
      border-bottom: 1px solid #E4E7EB;
      border-right: none;
    }
    .actionBox p {
      text-align: left;
    }
  }
}
.brands {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -5px;
  &__item {
    padding: 0 5px 10px;
    span {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 58px;
      height: 58px;
      padding: 4px;
      border: 1px solid rgba(228, 231, 235, .5);
      border-radius: 4px;
      background-color: #FFF;
    }
    img {
      display: block;
    }
    &--disable {
      opacity: .4;
      filter: grayscale(100%);
    }
  }
}

</style>
